//
// # Interaction Content Base (composition)
//
// The interaction tier of a content object: selection, change-log, undo/redo,
// mode dispatch. Unlike the render tier it does NOT extend `RenderContentBase`
// — it *holds* one (`render`) and forwards the handful of render-surface members
// it needs (`redraw`/`recalcBbox`/`bbox`/`children`/`contentKind`/
// `requestRedraw`) to it. A concrete content type is `X extends InteractionBase`
// that owns an `XRender`, so the server can build the render tier alone (no
// modes, no DOM) while the browser builds the interaction facade over it.
//
// The entity tree roots at the `render` object (entities' `parent`/`root`), not
// at this facade — nothing walks `.root` for anything but `requestRedraw`/
// `editor`, so that split is safe.
//

import { AsyncResult, Result } from "CoreUtils";
import { model } from "GenAlphaModel";
import * as changes from "DrawCore/src/changes";
import { ModeHandler } from "../modes";
import type { RedrawRequest } from "DrawCore/src/redraw";
import type { InteractionHost } from "../interaction_host";
import type { MousePos } from "DrawCore/src/mousepos";
import { EntityKind } from "DrawCore/src/entity";
import type { DrawTo } from "DrawCore/src/draw";
import type { FieldEdits } from "./fieldEdits";
import type {
  RenderContentBase,
  ChildInterface,
} from "DrawCore/src/content/base_render";
import { ContentKind } from "DrawCore/src/content/kind";

// Read a property off an unknown value without type assertions or `x is T`
// type-guard predicates (both lint-forbidden). Returns `undefined` when the
// value is not an indexable object. `Reflect.get` accepts an `object`, so we
// only need the (non-narrowing) object check.
function getField(value: unknown, key: string): unknown {
  if (typeof value !== "object" || value === null) return undefined;
  return Reflect.get(value, key);
}

// Read a numeric `id` off an arbitrary selected entity, if present.
function entityDataId(entity: unknown): number | undefined {
  const id = getField(getField(entity, "data"), "id");
  return typeof id === "number" ? id : undefined;
}

// # Interaction Content Base Class
//
// Generic over its render type `Render` so the concrete `contentKind` literal
// (and any other render-tier detail) flows through — keeping `Content` a
// discriminated union.
export abstract class InteractionBase<
  //
  Child extends ChildInterface,
  Selectable,
  Render extends RenderContentBase = RenderContentBase
> {
  constructor(
    readonly editor: InteractionHost,
    // The render tier this facade drives. Entities root here, not on `this`.
    readonly render: Render
  ) {}

  // Content-kind discriminant. A readonly *property* (not a getter) so `Content`
  // stays a discriminated union — TS doesn't narrow unions on getter
  // discriminants. Each content type declares its literal; it matches
  // `render.contentKind`.
  abstract readonly contentKind: ContentKind;

  // --- Forwarders to the render tier (so the body below reads unchanged) ---
  requestRedraw = (): RedrawRequest => this.render.requestRedraw();
  get children() {
    return this.render.children;
  }
  get bbox(): model.Bbox {
    return this.render.bbox;
  }
  // Arrow fields (bound to the instance): `redraw` is handed to `to.layer(...)`
  // as a bare callback in Editor.redraw, so it must not lose `this`. Before the
  // render/interaction split these were arrow-field members of the content class
  // for the same reason; keep that binding here.
  redraw = (to: DrawTo): unknown => this.render.redraw(to);
  recalcBbox = (): void => {
    this.render.recalcBbox();
  };
  // Invalidate cached style down the tree (e.g. on theme change). Cascades to
  // the render tier's children.
  invalidate = (): RedrawRequest => {
    this.render.children.forEach((child) => child.invalidate());
    return this.requestRedraw();
  };

  // The set of currently-selected entities.
  readonly selection = new Set<Child>();
  // The most recently selected entity, used for detail-panel focus.
  protected lastSelected_: Child | null = null;
  protected ident: model.Ident = model.Ident.default();

  // Public, read-only access to this content's ident (e.g. for context-menu
  // labels that depend on whether the design is a Depot or OpenAccess item).
  getIdent(): model.Ident {
    return this.ident;
  }

  // Abstract interaction attrs (render abstracts live in RenderContentBase).
  abstract idleMode(): ModeHandler;
  abstract applyChange(change: changes.Change<Child>): unknown;
  abstract whatdWeHit(mousePos: MousePos): Selectable | null;
  abstract addEntity(entity: Child, logChange: boolean): unknown; // Add a child entity
  abstract removeEntity(entity: Child, logChange: boolean): boolean; // Remove a child entity

  // Select all entities whose bounding boxes intersect the given bbox.
  // If `additive` is true, add to existing selection; otherwise replace it.
  // Default implementation does nothing. Content subclasses override as needed.
  selectAllInBbox = (
    _bbox: model.Bbox,
    _additive: boolean
  ): RedrawRequest | null => {
    return this.requestRedraw();
  };

  // Select every selectable entity in the content. Subclasses override.
  // Layout content selects different sets in idle vs DrawRoute mode, so the
  // dispatch lives on the mode handler — `Editor.selectAll` calls into the
  // mode, which calls back into the content.
  selectAll = (): RedrawRequest | null => {
    return this.requestRedraw();
  };

  // Convenience getter: the most recently selected entity, or null if nothing selected.
  get selected(): Child | null {
    if (this.selection.size === 0) return null;
    return this.lastSelected_;
  }

  // Delete all selected entities that are deletable.
  deleteSelected = (): RedrawRequest | null => {
    if (this.selection.size === 0) return null;

    // Collect removable entities and their removal changes.
    const removals: Array<changes.Single<Child>> = [];
    for (const entity of this.selection) {
      if (this.removeEntity(entity, true)) {
        // Wire segments are special: Wires.delete(wire, true) already logs
        // a Combo internally (Remove + any join changes). Skip logging here
        // to avoid sending a duplicate Remove to AlphaServer.
        if (entity.entityKind !== EntityKind.WireSegment) {
          removals.push(changes.Remove.new(entity));
        }
      }
    }
    if (removals.length === 0) return null;

    // Log all removals as a single atomic change (Combo if >1).
    if (removals.length === 1) {
      this.logChange(removals[0]);
    } else {
      const combo = changes.Combo.fromArray(removals);
      if (combo.ok) this.logChange(combo.val);
    }

    this.editor.goUiIdle();
    return this.deselect();
  };

  // Duplicate all selected entities that support duplication.
  duplicateSelected = (): RedrawRequest | null => null;

  // Compute the bounding box of the currently selected entities.
  selectionBbox = (): model.Bbox | null => null;

  // References to `Editor` attributes
  get canvas() {
    return this.editor.canvas;
  }
  get uiState() {
    return this.editor.uiState;
  }

  // Change-log, for undo-redo
  changeLog = new changes.ChangeLog<Child>();

  // Push a `Change` onto the undo stack. Does NOT send the change to the
  // platform — callers that originate a change locally should use logChange.
  protected recordChange(change: changes.Change<Child>): void {
    this.changeLog.add(change);
  }

  // Log a `Change` to the change history and to the platform.
  logChange(change: changes.Change<Child>): void {
    this.recordChange(change);
    this.editor.sendChangeMessage(change, this.contentKind);
  }

  // Merge the most recent `count` logged changes into a single Combo so they
  // undo/redo as one atomic step.
  combineRecentChanges(count: number): void {
    this.changeLog.combineLast(count);
  }

  // Cancel the placement of the single just-created entity: remove it locally,
  // tell the server to delete it, and drop the one changelog entry the create
  // pushed — hence dropLast(1).
  discardCreatedSelection(): void {
    for (const entity of this.selection) {
      this.removeEntity(entity, false);
      this.editor.sendChangeMessage(
        changes.Remove.new(entity),
        this.contentKind
      );
    }
    this.selection.clear();
    this.lastSelected_ = null;
    this.changeLog.dropLast(1); // Drop the speculative Add.
    this.notifySelection();
  }

  // Remove `entity` and record the removal on the undo log WITHOUT sending it to
  // the platform — for when the server has already applied the removal.
  removeEntityRecordOnly(entity: Child): boolean {
    const removed = this.removeEntity(entity, false);
    if (removed) this.recordChange(changes.Remove.new(entity));
    return removed;
  }

  // Roll back a record-only removal when a follow-up step fails.
  rollbackRecordOnlyRemoval(entity: Child): void {
    this.addEntity(entity, false);
    this.editor.sendChangeMessage(changes.Add.new(entity), this.contentKind);
    this.changeLog.dropLast(1);
  }

  // Undo the last change, if there is one.
  undo = (): RedrawRequest | null => {
    const inverseChange = this.changeLog.undo();
    if (!inverseChange) return null;
    this.applyChange(inverseChange);
    this.editor.sendChangeMessage(inverseChange, this.contentKind);
    this.syncSelectionToChange(inverseChange);
    return this.requestRedraw();
  };

  // Redo the last undone change, if there is one.
  redo = (): RedrawRequest | null => {
    const redoChange = this.changeLog.redo();
    if (!redoChange) return null;
    this.applyChange(redoChange);
    this.editor.sendChangeMessage(redoChange, this.contentKind);
    this.syncSelectionToChange(redoChange);
    return this.requestRedraw();
  };

  // Notify the host (via the Platform) that the selection changed.
  notifySelection(): void {
    if (this.selection.size === 0 || this.lastSelected_ === null) {
      this.editor.platform.notifySelectionChanged(null);
      return;
    }
    const entity = this.lastSelected_;
    this.editor.platform.notifySelectionChanged({
      entityKind: entity.entityKind,
      id: entityDataId(entity),
      count: this.selection.size,
    });
  }

  // Make `entity` the sole selected, highlighted entity.
  select = (entity: Child): RedrawRequest => {
    for (const e of this.selection) e.highlight(false);
    this.selection.clear();
    this.selection.add(entity);
    this.lastSelected_ = entity;
    const redraw = entity.highlight(true);
    this.notifySelection();
    return redraw;
  };

  // Find the already-selected entity equivalent to `entity`, or null.
  protected findSelectionMatch = (entity: Child): Child | null =>
    this.selection.has(entity) ? entity : null;

  // Toggle `entity` in the selection set.
  addToSelection = (entity: Child): RedrawRequest => {
    const existing = this.findSelectionMatch(entity);
    if (existing) {
      this.selection.delete(existing);
      existing.highlight(false);
      if (this.selection.size > 0) {
        let last: Child | null = null;
        for (const e of this.selection) last = e;
        this.lastSelected_ = last;
      } else {
        this.lastSelected_ = null;
      }
    } else {
      this.selection.add(entity);
      this.lastSelected_ = entity;
      entity.highlight(true);
    }
    this.notifySelection();
    return this.requestRedraw();
  };

  // Select multiple entities at once (e.g. from box-select).
  selectMultiple = (
    entities: Iterable<Child>,
    additive: boolean = false
  ): RedrawRequest => {
    if (!additive) {
      for (const entity of this.selection) entity.highlight(false);
      this.selection.clear();
      this.lastSelected_ = null;
    }
    for (const entity of entities) {
      if (this.findSelectionMatch(entity)) continue;
      this.selection.add(entity);
      this.lastSelected_ = entity;
      entity.highlight(true);
    }
    this.notifySelection();
    return this.requestRedraw();
  };

  // Capture a re-locatable key for each currently-selected entity.
  captureSelectionKeys = (): Array<{ entityKind: EntityKind; id: number }> => {
    const keys: Array<{ entityKind: EntityKind; id: number }> = [];
    const focus = this.lastSelected_;
    for (const entity of this.selection) {
      if (entity === focus) continue;
      const id = entityDataId(entity);
      if (id !== undefined) keys.push({ entityKind: entity.entityKind, id });
    }
    if (focus) {
      const id = entityDataId(focus);
      if (id !== undefined) keys.push({ entityKind: focus.entityKind, id });
    }
    return keys;
  };

  // Re-select the entities named by `keys`.
  restoreSelectionKeys = (
    keys: Array<{ entityKind: EntityKind; id: number }>
  ): void => {
    if (keys.length === 0) return;
    const byKey = new Map<string, Child>();
    for (const entity of this.selectableEntities()) {
      const id = entityDataId(entity);
      if (id !== undefined) byKey.set(`${entity.entityKind}:${id}`, entity);
    }
    const matches: Array<Child> = [];
    for (const key of keys) {
      const entity = byKey.get(`${key.entityKind}:${key.id}`);
      if (entity) matches.push(entity);
    }
    if (matches.length === 0) return;
    this.selectMultiple(matches);
  };

  // The entities in this content that can be selected and tracked by the
  // Properties panel. Base has none; content subclasses override.
  protected selectableEntities = (): Iterable<Child> => [];

  // Drop `entity` from the selection if it is currently selected.
  deselectIfSelected = (entity: Child): void => {
    if (!this.selection.delete(entity)) return;
    entity.highlight(false);
    if (this.lastSelected_ === entity) {
      let last: Child | null = null;
      for (const e of this.selection) last = e;
      this.lastSelected_ = last;
    }
    this.notifySelection();
  };

  // Recover a selectable entity from an `Update` change's type-erased `target`.
  protected selectableFromUpdateTarget = (_target: unknown): Child | null =>
    null;

  // Update the selection to reflect an undo/redo change that was just applied.
  private syncSelectionToChange(change: changes.Change<Child>): void {
    switch (change.kind) {
      case changes.Kind.Add:
        this.select(change.entity);
        break;
      case changes.Kind.Remove:
        this.deselectIfSelected(change.entity);
        break;
      case changes.Kind.Update: {
        const edited = this.selectableFromUpdateTarget(change.target);
        if (edited) this.select(edited);
        break;
      }
      case changes.Kind.Combo:
        change.changes.forEach((c) => {
          this.syncSelectionToChange(c);
        });
        break;
    }
  }

  // Deselect all highlighted entities.
  deselect = (): RedrawRequest | null => {
    for (const entity of this.selection) {
      entity.highlight(false);
    }
    this.selection.clear();
    this.lastSelected_ = null;
    this.notifySelection();
    return this.requestRedraw();
  };

  // Default "apply change request" - does nothing.
  applyChangeRequest = (_: Array<unknown>): AsyncResult<unknown> => {
    return Promise.resolve(Result.Ok(null));
  };

  // Apply a list of changes that the server has already applied on its end.
  applyServerOriginatedChange = (
    _rawChanges: Array<unknown>
  ): AsyncResult<unknown> => {
    return Promise.resolve(
      Result.Err({
        why: "not-implemented",
        message: `applyServerOriginatedChange not implemented for ${this.contentKind}`,
      })
    );
  };

  // Apply a batch of buffered field edits (from the Properties Panel).
  applyFieldEdits = (_edits: FieldEdits): Result<void, { why: string }> => {
    return Result.Err({ why: "field-edits-not-supported" });
  };

  // Forward `setMode` to the editor
  setMode = (mode: ModeHandler) => this.editor.setMode(mode);
}
